import './claim-entity.detail.contact.scss';
import './claim-entity.vendor.modal.scss';

import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import {
  VendorServiceInput,
  useAddVendorToContactMutation,
  useContactVendorsListQuery,
} from '@converge/graphql';

import { ApolloError } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import { NewConvergeButton } from '../../../presentation/components/new-button';
import { useConvergeSnackBar } from '../../../contexts';

// Vendor type interfaces
interface Vendor {
  name: string;
  uuid: string;
  selected: boolean;
}

interface VendorType {
  vendorCategory: string;
  uuid: string;
  vendors: Vendor[];
}

// GraphQL response structure
interface ServiceNodeVendor {
  uuid: string;
  selected: boolean;
  aliases: {
    edges: Array<{
      node: {
        uuid: string;
        name: string;
      };
    }>;
  };
}

interface ServiceNode {
  uuid: string;
  name: string;
  vendors: ServiceNodeVendor[];
}

interface ClaimServicesEdge {
  node: ServiceNode;
}

interface ClaimEntityVendorModalProps {
  open: boolean;
  onClose: () => void;
  onNoteSaved?: () => void;
  onSave?: (
    vendors: Array<{
      uuid: string;
      name: string;
      typeName: string;
      typeUuid: string;
    }>
  ) => void;
}

export const ClaimEntityVendorModal: FC<ClaimEntityVendorModalProps> = ({
  open,
  onClose,
  onSave,
  onNoteSaved,
}) => {
  const extractClaimUuidFromUrl = (
    url: string = window.location.pathname
  ): string => {
    const match = url.match(/\/claims\/([^/]+)/);
    return match ? match[1] : '';
  };
  const claimUuid = extractClaimUuidFromUrl();

  const { data, loading, refetch } = useContactVendorsListQuery(claimUuid);
  const { addVendorToContact } = useAddVendorToContactMutation();

  // Transform GraphQL data to match our component's expected format
  const vendorsList: VendorType[] =
    data?.claimServices?.edges?.map(({ node }: ClaimServicesEdge) => ({
      vendorCategory: node.name,
      uuid: node.uuid,
      vendors: node.vendors.map((vendor: ServiceNodeVendor) => ({
        uuid: vendor.uuid,
        name: vendor.aliases?.edges?.[0]?.node?.name || 'Unknown Vendor',
        selected: vendor.selected,
      })),
    })) || [];
  // State for selected vendors
  const [selectedVendors, setSelectedVendors] = useState<VendorServiceInput[]>(
    []
  );

  // State to track selected category
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Handle category selection
  const handleCategoryClick = (uuid: string) => {
    setSelectedCategory(uuid);
  };

  // Reset function to clear selections and category
  const resetSelections = () => {
    setSelectedVendors([]);
    setSelectedCategory(null);
  };

  // Enhanced close handler to reset selections before closing
  const handleClose = () => {
    resetSelections();
    onClose();
  };
  // Handle checkbox change
  const handleCheckboxChange = (
    vendor: Vendor,
    typeName = 'string',
    typeUuid = 'string'
  ) => {
    const vendorIndex = selectedVendors.findIndex(
      (item) => item.vendorUuid === vendor.uuid && item.serviceUuid === typeUuid
    );
    if (vendorIndex >= 0) {
      setSelectedVendors((prev) =>
        prev.filter(
          (item) =>
            !(item.vendorUuid === vendor.uuid && item.serviceUuid === typeUuid)
        )
      );
    } else {
      setSelectedVendors((prev) => [
        ...prev,
        {
          vendorUuid: vendor.uuid,
          claimUuid: claimUuid,
          serviceUuid: typeUuid,
        },
      ]);
    }
  };

  const handleSaveVendorSelections = async () => {
    if (selectedVendors.length === 0) {
      notify('No vendors selected', { variant: 'warning' });
      return;
    }

    let responseErrors;
    try {
      // Process all selected vendors

      await addVendorToContact(selectedVendors);

      // Log successful update
      notify(
        `Vendor${selectedVendors.length > 1 ? 's' : ''} successfully added`,
        {
          variant: 'success',
        }
      );

      // Trigger refresh if callback provided
      if (onNoteSaved) {
        onNoteSaved();
      }

      // Close the modal
      handleClose();
    } catch (cause) {
      console.error(`Failed to add vendors. Please try again. (${cause})`);
      if (cause instanceof ApolloError) {
        responseErrors = cause.graphQLErrors;
      }

      const errorMessage = responseErrors?.[0]?.message;
      notify(
        `Failed to add vendors. Please try again: ${
          errorMessage || 'Unknown error'
        }`,
        { variant: 'error' }
      );
    }
  };

  const { notify } = useConvergeSnackBar();

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        sx: {
          width: '900px',
          height: '674px',
          maxWidth: 'none',
          maxHeight: 'none',
          margin: 'auto',
        },
      }}
    >
      {/* <FormProvider {...formMethods}> */}
      <DialogTitle sx={{ padding: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingTop="10px"
          paddingRight="15px"
        >
          <Box className="main-title-vendor-modal">Select Vendors</Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent className="vendor-modal-container">
        {/* Two-Column Layout */}
        <Box
          className="vendor-two-column-layout"
          sx={{
            flex: '1 1 auto',
            display: 'flex',
            gap: 3,
            pb: 2,
            justifyContent: 'center',
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '50vh',
              }}
            >
              <CircularProgress />
            </Box>
          ) : vendorsList.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '50vh',
              }}
            >
              <Typography>No vendors available for this claim</Typography>
            </Box>
          ) : (
            <>
              {/* Left Side - Categories List */}
              <Box
                className="vendor-categories-column"
                sx={{
                  flex: '0 0 350px',
                  width: '350px',
                  height: '469px',
                  border: '1px solid #666',
                  overflowY: 'auto',
                  borderRadius: 1,
                }}
              >
                <List
                  disablePadding
                  sx={{
                    '& .MuiListItemButton-root': {
                      py: 1.5,
                      px: 2,

                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: '#e7fdfb',
                      },
                    },
                  }}
                >
                  {vendorsList.map((type: VendorType) => {
                    // Count previously saved vendors + newly selected vendors in current session
                    const previouslySelectedCount = type.vendors.filter(
                      (v) => v.selected
                    ).length;
                    const newlySelectedCount = selectedVendors.filter(
                      (item) => item.serviceUuid === type.uuid
                    ).length;
                    const selectedCount =
                      previouslySelectedCount + newlySelectedCount;

                    return (
                      <ListItemButton
                        key={type.uuid}
                        onClick={() => handleCategoryClick(type.uuid)}
                        selected={selectedCategory === type.uuid}
                        sx={{
                          backgroundColor:
                            selectedCategory === type.uuid
                              ? '#cefcf6 !important'
                              : 'transparent',
                        }}
                      >
                        <Typography className="vendor-type">
                          {type.vendorCategory}
                          {selectedCount > 0 && (
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                fontWeight: 600,
                                fontSize: '14px',
                              }}
                            >
                              ({selectedCount})
                            </Box>
                          )}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>

              <Box
                className="vendor-list-column"
                sx={{
                  flex: '0 0 350px',
                  width: '350px',
                  height: '437px',
                  border: '1px solid #666',
                  backgroundColor: selectedCategory ? '#FFFFFF' : '#F2F2F2',
                  p: 2,
                  overflowY: 'auto',
                  borderRadius: 1,
                }}
              >
                {!selectedCategory ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'black',
                        fontWeight: '400',
                        marginLeft: '100px',
                        marginRight: '100px',
                        fontSize: '16px',
                        textAlign: 'center',
                        fontFamily: 'Red Hat Text',
                      }}
                    >
                      Select a category on the left to start
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {vendorsList
                      .find((type) => type.uuid === selectedCategory)
                      ?.vendors.map((vendor: Vendor) => (
                        <ListItem key={vendor.uuid} disableGutters>
                          <FormControlLabel
                            control={
                              <Checkbox
                                disabled={vendor.selected === true}
                                data-testid="vendor-checkbox-btn"
                                checked={
                                  vendor.selected
                                    ? vendor.selected
                                    : selectedVendors.some(
                                        (item) =>
                                          item.vendorUuid === vendor.uuid &&
                                          item.serviceUuid === selectedCategory
                                      )
                                }
                                onChange={() =>
                                  handleCheckboxChange(
                                    vendor,
                                    vendorsList.find(
                                      (type) => type.uuid === selectedCategory
                                    )?.vendorCategory,
                                    selectedCategory
                                  )
                                }
                                name={vendor.uuid}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  '&.Mui-checked': {
                                    color: `${
                                      vendor.selected ? '#BDBDBD' : '#28C0A7'
                                    }`,
                                  },
                                }}
                              />
                            }
                            label={
                              <Box
                                className="vendor-list-name"
                                sx={{
                                  hyphens: 'auto',
                                  overflowWrap: 'break-word',
                                  maxWidth: '100%',
                                  minWidth: 0,
                                  display: 'block',
                                  letterSpacing: '0px',
                                  fontWeight: '600 !important',
                                }}
                              >
                                {vendor.name}
                              </Box>
                            }
                            sx={{
                              width: '100%',
                              margin: 0,
                              py: 0,
                              '.MuiFormControlLabel-label': {
                                width: 'calc(100% - 42px)',
                                ml: 0.5,
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                  </>
                )}
              </Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            mt: 'auto',
            py: 2.5,
            px: 2.5,
            backgroundColor: 'white',
          }}
        >
          <Box>
            <NewConvergeButton
              sx={{ width: '100px', marginRight: '20px' }}
              data-testid="vendor-cancel-btn"
              variant="outlined"
              label="cancel"
              onClick={handleClose}
            />
          </Box>
          <Box sx={{ width: '82px', marginRight: '36px' }}>
            <NewConvergeButton
              data-testid="vendor-save-btn"
              variant="contained"
              label="SAVE"
              onClick={handleSaveVendorSelections}
              disabled={selectedVendors.length === 0}
              sx={{
                bgcolor: '#8dd0c3',
                '&:hover': {
                  bgcolor: '#7bc0b3',
                },
                '&.Mui-disabled': {
                  bgcolor: '#cccccc',
                  color: '#666666',
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      {/* </FormProvider> */}
    </Dialog>
  );
};
